
import {
  Vue, Component, PropSync, Prop, Watch,
} from 'vue-property-decorator';
import DateRangePickerPortal from '@/features/common/components/DateRangePickerPortal.vue';
import { PeriodItemValueType, PeriodItemType } from '@/features/common/raw';
import { getLastDateRangeFromToday, formatCustomDateRangeForDisplay } from '@/features/common/helpers/moment';
import { VInput, VSelect } from '../types';

@Component({
  components: {
    DateRangePickerPortal,
  },
})
export default class CustomPeriodPicker extends Vue {
  @PropSync('value', { required: true }) syncedValue!: PeriodItemValueType | string;

  @Prop({ required: true }) items!: PeriodItemType[];

  @Prop({ required: false, default: false }) solid!: boolean;

  @Prop({ type: String, default: '' }) customRangeValue!: string;

  @Prop({
    type: Object,
    default: () => ({ fromDate: '', toDate: '' }),
  }) dateRange!: { fromDate: string, toDate: string };

  @Prop({ required: false, default: true }) isFuture!: boolean;

  @Prop({ required: false, default: false }) enforceRangeOrder!: boolean;

  private period: PeriodItemValueType = 'Last30Days';

  private showCalendar = false;

  private customRange = this.customRangeValue;

  private instanceId = `custom-period-picker-${Math.random().toString(36).substr(2, 9)}`;

  get syncedCustomRange(): string {
    return this.customRangeValue;
  }

  set syncedCustomRange(value: string) {
    this.$emit('update:customRangeValue', value);
  }

  get syncedDateRange(): { fromDate: string, toDate: string } {
    return this.dateRange;
  }

  set syncedDateRange(value: { fromDate: string, toDate: string }) {
    this.$emit('update:dateRange', value);
  }

  get itemsWithCustom(): PeriodItemType[] {
    const hasCustom = this.items.some((item) => item.value === 'Custom');
    if (hasCustom) {
      return this.items;
    }
    return [
      ...this.items,
      {
        value: 'Custom',
        displayName: 'Custom Range',
      },
    ];
  }

  get previouslySelectedDates(): string[] {
    if (this.dateRange.fromDate && this.dateRange.toDate) {
      return [this.dateRange.fromDate, this.dateRange.toDate];
    }
    return [];
  }

  get clickOutsideConfig() {
    return this.handleClickOutside;
  }

  get formattedCustomRange(): string {
    return formatCustomDateRangeForDisplay(this.customRange);
  }

  @Watch('syncedValue', { immediate: true })
  changedSyncedValue(newVal: PeriodItemValueType): void {
    this.period = newVal;
  }

  @Watch('customRangeValue')
  changedCustomRangeValue(newVal: string): void {
    this.customRange = newVal;
  }

  mounted(): void {
    const { select } = this.$refs;
    if (!select) return;

    const { input: selectInput } = (select as VInput).$refs;
    if (!selectInput) return;

    (selectInput as HTMLInputElement).addEventListener('focus', this.focusHandler, true);
  }

  private changePeriod(value: PeriodItemValueType) {
    this.syncedValue = value;
    this.showCalendar = value === 'Custom';

    if (value === 'Custom') return;

    // Clear custom range when switching to a standard period
    this.customRange = '';
    this.syncedCustomRange = '';

    const dateRange = this.convertDateStringToRange(value);
    this.syncedDateRange = dateRange;
    this.$emit('change', dateRange, value);
  }

  private focusHandler() {
    const { select } = this.$refs;
    if (!select) return;

    (select as VSelect).isMenuActive = true;
  }

  private async applyDateRangeFilter(calendarData: string[]): Promise<void> {
    this.showCalendar = false;
    const [fromDate, toDate] = calendarData;
    this.customRange = calendarData.join('&nbsp;&nbsp;-&nbsp;&nbsp;');
    this.syncedCustomRange = this.customRange;

    const dateRange = {
      fromDate,
      toDate: toDate || fromDate,
    };

    this.syncedDateRange = dateRange;
    this.$emit('change', dateRange, 'Custom');
  }

  private closeCustomRangePicker() {
    this.syncedValue = 'Last7Days';
    this.customRange = '';
    this.syncedCustomRange = '';

    const dateRange = this.convertDateStringToRange('Last7Days');
    this.syncedDateRange = dateRange;
    this.$emit('change', dateRange, 'Last7Days');
  }

  private handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target) {
      this.closeDatePicker();
      return;
    }

    const isClearButton = target.closest('.v-input__icon--clear')
                         || target.closest('button[aria-label*="clear"]')
                         || target.classList.contains('v-input__icon--clear')
                         || target.closest('.v-input__append-inner');

    if (isClearButton) {
      return;
    }

    const datePickerPortal = document.querySelector('.date-picker');
    if (datePickerPortal && datePickerPortal.contains(target)) {
      return;
    }

    this.closeDatePicker();
  }

  public closeDatePicker() {
    this.showCalendar = false;
    // If user closes without applying, keep the current period
    if (!this.customRange && this.period === 'Custom') {
      this.syncedValue = 'Last7Days';
      const dateRange = this.convertDateStringToRange('Last7Days');
      this.syncedDateRange = dateRange;
      this.$emit('change', dateRange, 'Last7Days');
    }
  }

  private reopenCustomRangePicker() {
    this.showCalendar = true;
  }

  private convertDateStringToRange = (dateString: PeriodItemValueType): {
    fromDate: string,
    toDate: string,
  } => {
    return getLastDateRangeFromToday(dateString);
  }
}
